import { useEffect, useRef } from 'react'

import { color } from '../../theme/tokens'
import type { OrbState } from '../../lib/callEngine'

/**
 * Orb de presença.
 *
 * Quando existe áudio real (microfone ou fluxo remoto da Realtime API), os anéis
 * reagem à amplitude medida. Sem áudio, cai para uma amplitude sintética — o
 * movimento continua legível na sala mesmo no modo simulado.
 *
 * A animação roda fora do React (mutação direta de estilo em rAF) para não
 * disparar 60 renders por segundo em uma tela com muitos painéis.
 */
export function Orb({
  state,
  amplitudeRef,
}: {
  state: OrbState
  amplitudeRef: React.MutableRefObject<(() => number) | null>
}) {
  const ring1 = useRef<HTMLDivElement>(null)
  const ring2 = useRef<HTMLDivElement>(null)
  const ring3 = useRef<HTMLDivElement>(null)
  const core = useRef<HTMLDivElement>(null)
  const stateRef = useRef(state)
  stateRef.current = state

  useEffect(() => {
    let frame = 0
    const rings = [ring1, ring2, ring3]

    const loop = () => {
      frame = requestAnimationFrame(loop)
      const current = stateRef.current
      const read = amplitudeRef.current

      let amplitude: number
      if (read) {
        amplitude = read()
      } else if (current === 'ai' || current === 'cli') {
        const t = performance.now() / 1000
        amplitude = Math.abs(
          Math.sin(t * 5.1) * 0.5 + Math.sin(t * 8.7) * 0.3 + Math.sin(t * 13.3) * 0.2,
        )
      } else {
        amplitude = 0.06 + Math.sin(performance.now() / 900) * 0.04
      }

      const speaking = current === 'ai' || current === 'cli'
      // Azul quando é o cliente falando, laranja quando é a IA.
      const hue = current === 'cli' ? '47,111,214' : '240,134,47'
      const direction = current === 'cli' ? -1 : 1

      rings.forEach((ref, index) => {
        const element = ref.current
        if (!element) return
        const phase = amplitude * (1 - index * 0.22) * direction
        element.style.transform = `scale(${1 + phase * 0.16})`
        element.style.opacity = speaking
          ? String(0.35 + amplitude * 0.65 - index * 0.08)
          : '0.22'
        element.style.borderColor = `rgba(${hue},${(speaking ? 0.8 : 0.3) - index * 0.16})`
      })

      const centre = core.current
      if (centre) {
        centre.style.transform = `scale(${1 + amplitude * (speaking ? 0.07 : 0.015)})`
        centre.style.boxShadow = `0 0 ${22 + amplitude * 34}px rgba(${hue},${
          0.18 + amplitude * 0.35
        }), inset 0 2px 10px rgba(255,255,255,.14)`
        centre.style.animation = current === 'think' ? 'shim 1.6s ease-in-out infinite' : 'none'
      }
    }

    frame = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(frame)
  }, [amplitudeRef])

  return (
    <div
      style={{
        position: 'relative',
        width: 186,
        height: 186,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        ref={ring3}
        style={{
          position: 'absolute',
          width: 186,
          height: 186,
          borderRadius: '50%',
          border: '1.5px solid rgba(240,134,47,.3)',
          transition: 'opacity .18s linear, border-color .3s linear',
        }}
      />
      <div
        ref={ring2}
        style={{
          position: 'absolute',
          width: 150,
          height: 150,
          borderRadius: '50%',
          border: '2px solid rgba(240,134,47,.5)',
          transition: 'opacity .18s linear, border-color .3s linear',
        }}
      />
      <div
        ref={ring1}
        style={{
          position: 'absolute',
          width: 118,
          height: 118,
          borderRadius: '50%',
          border: '2.5px solid rgba(240,134,47,.75)',
          transition: 'opacity .18s linear, border-color .3s linear',
        }}
      />
      <div
        ref={core}
        style={{
          position: 'relative',
          width: 88,
          height: 88,
          borderRadius: '50%',
          background: `radial-gradient(40% 40% at 35% 30%, #4a5a72, ${color.panel} 70%, #222c3b)`,
          boxShadow: '0 0 30px rgba(240,134,47,.25), inset 0 2px 10px rgba(255,255,255,.14)',
        }}
      />
    </div>
  )
}
